import { App, Button, Collapse, Divider, Empty, Spin } from "antd"
import React, { Suspense, lazy, useState } from "react"
import { useNavigate } from "react-router-dom"

import { FilePreviewModal } from "../../../../../components/FilePreviewModal/FilePreviewModal.tsx"
import { RoutesPaths } from "../../../../../constants/routes.ts"
import { useAppDispatch } from "../../../../../hooks/use-app-dispatch.ts"
import { showErrorNotifications } from "../../../../../utils/handlers/handle-error"
import {
  getContractById,
  previewContract
} from "../../../../Contracts/api/services"
import {
  duplicateContract,
  emailContractToClient,
  markContractAsSent,
  submitContractForReview
} from "../../../../Contracts/redux/contracts.thunks.ts"
import { type Contract } from "../../../../Contracts/types/types.ts"
import { fetchContractDocumentById } from "../../../../DocumentTemplates/redux/thunks/document-contract.thunks.ts"
import { fetchDocumentPreview } from "../../../../DocumentTemplates/redux/thunks/document-templates.thunks.ts"
import type {
  LeadContract,
  LeadDocumentAttachment
} from "../../../types/lead.documents.types.ts"
import { LeadViewContractAttachments } from "./LeadViewContractAttachments.tsx"
import { LeadViewContractItem } from "./LeadViewContractItem.tsx"

const LeadViewContactView = lazy(
  () => import("../LeadViewDocument/LeadViewDocumentView.tsx")
)

interface LeadViewContractsProps {
  isUpdateAllowed: boolean
  viewOnly: boolean
  contracts?: LeadContract[] | null
}

const INITIAL_VISIBLE_COUNT = 5

export const LeadViewContracts = ({
  isUpdateAllowed,
  viewOnly,
  contracts
}: LeadViewContractsProps) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { modal } = App.useApp()

  const [previewFile, setPreviewFile] = useState<null | LeadDocumentAttachment>(
    null
  )
  const [previewHTML, setPreviewHTML] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  const totalCount = contracts?.length ?? 0
  const hasMore = totalCount > INITIAL_VISIBLE_COUNT
  const hiddenCount = totalCount - INITIAL_VISIBLE_COUNT

  const initialContracts = contracts?.slice(0, INITIAL_VISIBLE_COUNT)
  const additionalContracts = contracts?.slice(INITIAL_VISIBLE_COUNT)

  const onSendToReview = async (contractId: string, attachmentId: string) => {
    await dispatch(submitContractForReview({ contractId, attachmentId }))
  }

  const onMarkAsSent = async (contractId: string) => {
    await dispatch(markContractAsSent(contractId))
  }

  const onShare = async (contract: LeadContract) => {
    await dispatch(emailContractToClient(contract.id))
  }

  const onViewContract = async (contract: LeadContract) => {
    try {
      const contract_id = contract?.id

      if (contract?.is_version_2) {
        const document = await dispatch(
          fetchContractDocumentById(contract_id)
        )?.unwrap()

        const previewHTML = await dispatch(
          fetchDocumentPreview({
            ...document,
            contract_id,
            _meta: { doc_type: "contract" }
          })
        )?.unwrap()

        setPreviewHTML(previewHTML)
      } else {
        const contractRes = await getContractById(contract_id)
        if (!contractRes?.ok) throw new Error("Failed to fetch contract")
        const fullContract = contractRes?.data as Contract

        const previewRes = await previewContract({
          contract_id,
          visible_blocks: fullContract?.visible_blocks,
          ...fullContract?.contract_data
        })
        if (!previewRes?.ok)
          throw new Error("Failed to fetch contracts preview")

        const html = previewRes?.data as string
        if (!html) throw new Error("Failed to fetch contract preview HTML")

        setPreviewHTML(html)
      }
    } catch (e: unknown) {
      showErrorNotifications(e)
    }
  }

  const onDuplicateContract = async (contract: LeadContract) => {
    modal.confirm({
      centered: true,
      title: "Confirm duplication",
      content: "Are you sure you want to duplicate this contract?",
      cancelText: "Cancel",
      okText: "Duplicate",
      onOk: async () => {
        const newContract = await dispatch(
          duplicateContract(contract?.id)
        )?.unwrap()

        if (!newContract) return

        const href = contract?.is_version_2
          ? RoutesPaths.contractDocument.edit(newContract?.id)
          : RoutesPaths.contract.edit(newContract?.id)

        navigate(href)
      }
    })
  }

  const renderContractItem = (contract: LeadContract) => ({
    key: contract?.id,
    showArrow: false,
    headerClass: "group",
    label: (
      <LeadViewContractItem
        isUpdateAllowed={isUpdateAllowed}
        viewOnly={viewOnly}
        contract={contract}
        onViewContract={onViewContract}
        onViewPDF={setPreviewFile}
        onDuplicateContract={onDuplicateContract}
        onSendToReview={onSendToReview}
        onMarkAsSent={onMarkAsSent}
        onShare={onShare}
      />
    ),
    children: contract?.attachments?.length ? (
      <LeadViewContractAttachments
        attachments={contract.attachments}
        onViewPDF={setPreviewFile}
      />
    ) : (
      <Empty description="No attachments" />
    )
  })

  return (
    <>
      {previewHTML && (
        <Suspense fallback={<Spin fullscreen spinning />}>
          <LeadViewContactView
            title="Contract Preview"
            onClose={() => setPreviewHTML(null)}
            previewHTML={previewHTML}
          />
        </Suspense>
      )}

      {previewFile && (
        <FilePreviewModal
          visible
          doc={{
            file: previewFile?.file_url,
            name: previewFile?.file_name,
            version: previewFile?.version
          }}
          onClose={() => setPreviewFile(null)}
        />
      )}

      <Collapse items={initialContracts?.map(renderContractItem)} />

      {hasMore && (
        <>
          <div
            className="grid transition-all duration-300 ease-in-out"
            style={{
              gridTemplateRows: showAll ? "1fr" : "0fr"
            }}
          >
            <div className="overflow-hidden">
              <Collapse items={additionalContracts?.map(renderContractItem)} />
            </div>
          </div>

          <Divider className="my-2">
            <Button
              type="link"
              onClick={() => setShowAll((prev) => !prev)}
              className="text-sm"
            >
              {showAll ? "Show less" : `Show more (${hiddenCount})`}
            </Button>
          </Divider>
        </>
      )}
    </>
  )
}
